'use client';
// @ts-nocheck

// Compatibility shim for the Base44 SDK.
//
// Legacy components import { base44 } from '@/api/base44Client' and call
// methods like base44.entities.Post.filter({...}). This file exposes the
// same surface but routes everything through Supabase + our /api routes,
// so pages can be ported one-by-one without rewriting their data calls.
//
// Surface implemented:
//   base44.entities.<EntityName>.filter(where, sortKey?, limit?)
//   base44.entities.<EntityName>.list(sortKey?, limit?)
//   base44.entities.<EntityName>.get(id)
//   base44.entities.<EntityName>.create(data)
//   base44.entities.<EntityName>.update(id, data)
//   base44.entities.<EntityName>.delete(id)
//   base44.entities.<EntityName>.subscribe(callback) -> unsubscribe
//   base44.auth.me()
//   base44.auth.logout(redirectTo?)
//   base44.auth.loginViaEmailPassword(email, password)
//   base44.auth.register({ email, password })
//   base44.auth.updateMe(updates)
//   base44.auth.redirectToLogin(returnTo?)
//   base44.functions.invoke(name, payload?)
//
// Differences from the real Base44 SDK:
// - sortKey 'created_date'/'updated_date' is rewritten to 'created_at'/
//   'updated_at' since that's our schema
// - updateMe maps banner_url -> header_image; drops 'website' (Base44 had it
//   on the User; we'll move to the links jsonb later)
// - subscribe events are { type: 'create'|'update'|'delete', data: row }
//   matching Base44's shape, translated from Supabase Realtime payloads
// - functions.invoke(name) hits /api/<name> on the same origin (Vercel
//   functions); 404 until the function is ported

import { createClient } from '@/lib/supabase/client';

const supabase = createClient();

// ───── name mapping ──────────────────────────────────────────────────────

// Base44 used `User` as the entity for profile data; we folded it into the
// `profiles` table that bridges auth.users. Like / Save use post-prefixed
// table names since `like` and `save` are SQL reserved words.
const ENTITY_OVERRIDES = {
  User: 'profiles',
  Like: 'post_like',
  Save: 'post_save',
};

function pascalToSnake(name) {
  return name
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1_$2')
    .toLowerCase();
}

function tableFor(entityName) {
  return ENTITY_OVERRIDES[entityName] ?? pascalToSnake(entityName);
}

// ───── query helpers ─────────────────────────────────────────────────────

function parseSortKey(key) {
  if (!key) return null;
  let column = key;
  let ascending = true;
  if (key.startsWith('-')) {
    column = key.slice(1);
    ascending = false;
  }
  // Base44 used created_date / updated_date naming
  if (column === 'created_date') column = 'created_at';
  else if (column === 'updated_date') column = 'updated_at';
  return { column, ascending };
}

function applyWhere(query, where) {
  for (const [key, value] of Object.entries(where || {})) {
    if (value === undefined) continue;
    if (Array.isArray(value)) query = query.in(key, value);
    else if (value === null) query = query.is(key, null);
    else if (value && typeof value === 'object') {
      // Base44-style operator objects, e.g. { $regex: 'foo' }. Without this a
      // filter like { conversation_id: { $regex: email } } fell through to
      // .eq(column, {...}) and silently matched nothing (e.g. the inbox
      // conversation list came up empty).
      if ('$regex' in value) query = query.ilike(key, `%${value.$regex}%`);
      else if ('$ne' in value) query = query.neq(key, value.$ne);
      else if ('$gt' in value) query = query.gt(key, value.$gt);
      else if ('$gte' in value) query = query.gte(key, value.$gte);
      else if ('$lt' in value) query = query.lt(key, value.$lt);
      else if ('$lte' in value) query = query.lte(key, value.$lte);
      else if ('$in' in value) query = query.in(key, value.$in);
      // Not an operator object (e.g. a jsonb-equality filter) → old behaviour.
      else query = query.eq(key, value);
    }
    else query = query.eq(key, value);
  }
  return query;
}

function unwrap({ data, error }) {
  if (error) throw error;
  return data;
}

// ───── entity api ────────────────────────────────────────────────────────

function createEntityApi(entityName) {
  const table = tableFor(entityName);
  // Cross-user reads of profiles go through the public_profile view, which
  // exposes only non-sensitive columns (no DOB / role / stripe_account_id).
  // Writes + self-reads (base44.auth.me) use the base profiles table, which
  // RLS now restricts to the caller's own row. Realtime stays on the base
  // table (Postgres realtime doesn't publish views).
  const readTable = table === 'profiles' ? 'public_profile' : table;

  return {
    async filter(where, sortKey, limit) {
      let q = supabase.from(readTable).select('*');
      q = applyWhere(q, where);
      const sort = parseSortKey(sortKey);
      if (sort) q = q.order(sort.column, { ascending: sort.ascending });
      if (limit) q = q.limit(limit);
      return unwrap(await q);
    },

    async list(sortKey, limit) {
      let q = supabase.from(readTable).select('*');
      const sort = parseSortKey(sortKey);
      if (sort) q = q.order(sort.column, { ascending: sort.ascending });
      if (limit) q = q.limit(limit);
      return unwrap(await q);
    },

    async get(id) {
      return unwrap(
        await supabase.from(readTable).select('*').eq('id', id).maybeSingle(),
      );
    },

    async create(data) {
      return unwrap(
        await supabase.from(table).insert(data).select().single(),
      );
    },

    // Insert that silently no-ops if the row would violate a unique
    // constraint — useful for "record once" tables like post_view, where
    // every re-mount of a card would otherwise hit the (user, post)
    // unique index and bounce off as a 409. Returns the inserted row on
    // success, or `null` when the upsert was skipped (row already
    // existed). Pass `onConflict` as the comma-joined constraint
    // columns (e.g. "user_email,post_id").
    async createIfNew(data, { onConflict } = {}) {
      const { data: rows, error } = await supabase
        .from(table)
        .upsert(data, { onConflict, ignoreDuplicates: true })
        .select();
      if (error) throw error;
      return rows?.[0] ?? null;
    },

    async update(id, data) {
      return unwrap(
        await supabase.from(table).update(data).eq('id', id).select().single(),
      );
    },

    async delete(id) {
      const { error } = await supabase.from(table).delete().eq('id', id);
      if (error) throw error;
      return { success: true };
    },

    subscribe(callback) {
      const channel = supabase
        .channel(`entity-${table}-${Math.random().toString(36).slice(2, 10)}`)
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table },
          (payload) => {
            const eventType = payload.eventType.toLowerCase();
            callback({
              type: eventType === 'insert' ? 'create' : eventType,
              data: payload.new ?? payload.old,
            });
          },
        )
        .subscribe();
      return () => {
        supabase.removeChannel(channel);
      };
    },
  };
}

// Proxy so we don't have to enumerate every entity name. Each entity API is
// created on first access and cached.
const entitiesProxy = new Proxy(
  {},
  {
    get(target, name) {
      if (typeof name !== 'string' || name in target) return target[name];
      target[name] = createEntityApi(name);
      return target[name];
    },
  },
);

// ───── auth ──────────────────────────────────────────────────────────────

async function getCurrentUser() {
  const { data, error } = await supabase.auth.getUser();
  if (error) throw error;
  return data.user;
}

const auth = {
  async me() {
    const user = await getCurrentUser();
    if (!user) throw new Error('Not authenticated');

    const { data: profile, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .maybeSingle();
    if (error) throw error;

    // Merge so the Base44-style fields (full_name, avatar_url, username,
    // onboarding_completed, etc.) are all on one object alongside email/id.
    return {
      ...(profile ?? {}),
      id: user.id,
      email: user.email,
    };
  },

  async logout(redirectTo) {
    await supabase.auth.signOut();
    if (redirectTo && typeof window !== 'undefined') {
      window.location.href = redirectTo;
    }
  },

  async loginViaEmailPassword(email, password) {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  },

  async register({ email, password }) {
    const { error } = await supabase.auth.signUp({ email, password });
    if (error) throw error;
  },

  async updateMe(updates) {
    const user = await getCurrentUser();
    if (!user) throw new Error('Not authenticated');

    // Translate legacy field names to our schema.
    const mapped = { ...updates };
    if ('banner_url' in mapped) {
      mapped.header_image = mapped.banner_url;
      delete mapped.banner_url;
    }
    if ('website' in mapped) {
      // Base44 stored website on the User. Our schema has a `links` jsonb
      // for arbitrary custom links; folding 'website' into it is a porting
      // task for whoever owns the Settings page. Drop for now.
      delete mapped.website;
    }

    // Only let the client write self-editable profile fields. Identity and
    // entitlement columns (email, role, verified, verification_plan, is_dealer,
    // stripe_account_id, the denormalized counts) are server/trigger-managed —
    // the DB also REVOKEs UPDATE on them from the client role (migration 0103),
    // this allowlist is the matching defense in depth so a stray privileged
    // field doesn't fail the whole update.
    const EDITABLE_PROFILE_FIELDS = new Set([
      'username', 'full_name', 'avatar_url', 'header_image', 'bio', 'location',
      'profile_category', 'gender', 'date_of_birth', 'links', 'style_tags',
      'onboarding_completed',
    ]);
    const safe = {};
    for (const [k, v] of Object.entries(mapped)) {
      if (EDITABLE_PROFILE_FIELDS.has(k)) safe[k] = v;
    }
    if (Object.keys(safe).length === 0) return;

    const { error } = await supabase
      .from('profiles')
      .update(safe)
      .eq('id', user.id);
    if (error) throw error;
  },

  redirectToLogin(returnTo) {
    if (typeof window === 'undefined') return;
    const next = returnTo ? `?next=${encodeURIComponent(returnTo)}` : '';
    window.location.href = `/login${next}`;
  },
};

// ───── functions ─────────────────────────────────────────────────────────

async function invoke(name, payload) {
  const res = await fetch(`/api/${name}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload ?? {}),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    const err = new Error(
      `base44.functions.invoke('${name}') -> ${res.status} ${res.statusText}: ${text}`,
    );
    err.status = res.status;
    throw err;
  }
  // Base44's original SDK wrapped responses as { data }, and existing
  // callers still read res.data?.foo. Preserve that shape.
  const text = await res.text();
  return { data: text ? JSON.parse(text) : null };
}

// ───── integrations ──────────────────────────────────────────────────────

const integrations = {
  Core: {
    // Upload a File or Blob to R2. The browser PUTs the bytes directly to
    // R2 via a one-time presigned URL — videos and other large files would
    // otherwise hit Vercel's 4.5 MB serverless function body limit. The
    // server's only job is to sign the URL.
    //
    // Returns { file_url } pointing at the public CDN URL — same shape as
    // before, so call-sites don't change.
    async UploadFile({ file }) {
      const filename = file.name || 'upload.bin';
      const contentType = file.type || 'application/octet-stream';

      const signRes = await fetch('/api/uploadFileUrl', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename, contentType, size: file.size }),
      });
      if (!signRes.ok) {
        const text = await signRes.text().catch(() => '');
        throw new Error(`UploadFile (sign) failed: ${signRes.status} ${text}`);
      }
      const { uploadUrl, fileUrl } = await signRes.json();

      // Browser PUTs straight to R2. The Content-Type here MUST match the
      // contentType we sent to the signer; R2 includes it in the signature.
      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': contentType },
        body: file,
      });
      if (!putRes.ok) {
        const text = await putRes.text().catch(() => '');
        throw new Error(`UploadFile (PUT) failed: ${putRes.status} ${text}`);
      }

      // Content moderation. The bytes went straight to R2 (presigned PUT), so
      // the server moderates by URL afterwards and deletes the object if it's
      // flagged. Images + videos only. A flagged upload throws (with a
      // `.moderation` marker) so callers can show a friendly message; a network
      // error talking to the moderator fails open (upload allowed).
      const isImage = contentType.startsWith('image/');
      const isVideo = contentType.startsWith('video/');
      if (isImage || isVideo) {
        try {
          const modRes = await fetch('/api/moderateContent', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(isVideo ? { video_url: fileUrl } : { image_url: fileUrl }),
          });
          const mod = await modRes.json().catch(() => null);
          if (mod && mod.safe === false) {
            const err = new Error('This media was flagged as inappropriate and was not uploaded.');
            err.moderation = true;
            err.reason = mod.reason || null;
            throw err;
          }
        } catch (e) {
          if (e?.moderation) throw e;
          // Couldn't reach the moderator — allow the upload rather than block.
          console.error('moderation check failed (allowing upload):', e?.message || e);
        }
      }

      return { file_url: fileUrl };
    },

    // Transactional email via /api/sendEmail (Resend). Two modes:
    //   { to, template, props }      — render a React Email template
    //   { to, subject, body|html }   — free-form
    // Auth-gated; From is forced to RESEND_FROM_EMAIL.
    /** @param {{ to: string, subject?: string, body?: string, html?: string, replyTo?: string, template?: string, props?: Record<string, unknown> }} args */
    async SendEmail({ to, subject, body, html, replyTo, template, props }) {
      const res = await fetch('/api/sendEmail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to, subject, body, html, replyTo, template, props }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`SendEmail failed: ${res.status} ${text}`);
      }
      return res.json();
    },
  },
};

// ───── exports ───────────────────────────────────────────────────────────

export const base44 = {
  entities: entitiesProxy,
  auth,
  functions: { invoke },
  integrations,
};
